<script>
    import { onMount } from "svelte";

    import {
        isMobile,
        trafficAccidentAuthorities,
        workersCompensation,
    } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SearchSelect,
        PageLoading,
        Button,
        MultiSearchSelect,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import PatientEncounterBulkActionsModal from "./components/PatientEncounterBulkActionsModal.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        BillingStatus,
        claimStatus,
        InsuranceType,
        encounterBillingType,
        DirectInsuranceType,
        encounterStatus,
        encounterPaymentDropdownOptions,
        DirectEncounterStatus,
    } from "../../Utils/utilEnums.svelte";

    import {
        goToURL,
        replaceURL,
        getClaimStatus,
        getText,
        getTitle,
        getFormatDateAndTime,
        showError,
        isObjectEmpty,
        getInsuranceType,
        getUrlWithoutSearch,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getEncounterStatus,
        getBillingStatus,
        useTableScrollMemory,
    } from "../../Utils/utilFunctions.svelte";

    import { user, loggedInDoctorDetail, insuranceFund } from "../../stores";

    import {
        defaultTableRowBgColor,
        duplicateIcon,
        editIcon,
    } from "../../Utils/utilConstants.svelte";

    import {
        cacheInsuranceFunds,
        cacheTrafficAccidentAuthorities,
        cacheWorkersCompensation,
    } from "../../Utils/utilCache.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import SurgeonSelector from "../../Components/Common/SurgeonSelector.svelte";

    import CustomizeTable from "../../Components/Custom/Table/CustomizeTable.svelte";
    import BasicInfo from "../../Components/Custom/Table/Components/EncounterTable/BasicInfo.svelte";
    import Checkbox from "../../Components/Custom/Table/Components/EncounterTable/Checkbox.svelte";
    import InsuranceValidationInfo from "../../Components/Custom/Table/Components/EncounterTable/InsuranceValidationInfo.svelte";
    import DoctorInfo from "../../Components/Custom/Table/Components/EncounterTable/DoctorInfo.svelte";
    import SurgeonInfo from "../../Components/Custom/Table/Components/EncounterTable/SurgeonInfo.svelte";
    import ScheduledDateInfo from "../../Components/Custom/Table/Components/EncounterTable/ScheduledDateInfo.svelte";
    import BillingStatusInfo from "../../Components/Custom/Table/Components/EncounterTable/BillingStatusInfo.svelte";
    import EncounterStatusInfo from "../../Components/Custom/Table/Components/EncounterTable/EncounterStatusInfo.svelte";
    import TableActions from "../../Components/Custom/Table/Components/EncounterTable/TableActions.svelte";
    import ClaimStatusInfo from "../../Components/Custom/Table/Components/EncounterTable/ClaimStatusInfo.svelte";

    import { syncEncounterPaymentDropdownOptionFilter } from "./helpers/encounterHelper.svelte";
    import DataId from "../../Components/Custom/Table/Components/EncounterTable/DataId.svelte";
    import HospitalInfo from "../../Components/Custom/Table/Components/EncounterTable/HospitalInfo.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";
    import DropdownMenu from "../../Components/Common/DropdownMenu.svelte";
    import UploadPdfModal from "../../Components/Common/UploadPdfModal.svelte";

    export let params = {};

    let isGettingPatientEncounters = false,
        isLoadingHealthFund = false,
        isLoadingWorkCoverAuthorityFund = false,
        isLoadingTrafficAccidentAuthority = false,
        isLoadingAccountDoctors = false,
        isLoadingHospitals = false,
        isUploadListModalOpen = false,
        returnPatientEncounterArr = [],
        accountDoctorsArr = [],
        hospitalsOptionsArr = [],
        filters = {
            status: "0,1,5,7,8",
            billingStatus: "0,1,2,3,4",
            handOver: "",
            claimStatus: "",
            paymentOption: "",
            badDebtor: Number(-1),
        },
        page = 0,
        pageSize = 25,
        count,
        checkboxString = "",
        selectedPatientEncounters = [],
        selectedPatientEncountersIdArr = [],
        filterSurgeonObj,
        showBulkActionsModal = false,
        bulkActionType = "",
        currentDoctorId,
        bulkChangeSurgeonButtonToolTip = "",
        showFilters = false;

    const getEncounterStatusArrayFromFilter = (statusValue) => {
        if (statusValue === undefined || statusValue === null || statusValue === "")
            return [];

        return statusValue
            .toString()
            .split(",")
            .map((value) => Number(value))
            .filter((value) => !isNaN(value));
    };

    let searchEncounterStatusArr = getEncounterStatusArrayFromFilter(
        filters.status,
    );

    let searchTypeIfBillArr = [];

    let searchHospitalsArr = [];

    let searchInsuranceTypeArr = [];

    let searchPrivateHealthFundArr = [],
        healthFundOptionsArr = [],
        isShowPrivateHealthFundSelect = false;

    let searchWorkCoverAuthorityFundArr = [],
        workCoverAuthorityOptionsArr = [],
        isShowWorkCoverAuthoritySelect = false;

    let searchTrafficAccidentFundArr = [],
        trafficAccidentOptionsArr = [],
        isShowTrafficAccidentSelect = false;

    let selectedAccountDoctors = [],
        selectedDoctors = [],
        primarySurgeonSelectorRef,
        secondarySurgeonSelectorRef;

    $: isUserDoctor = $user.role == 2;

    $: isUserDoctorOrPatient = $user.role == 1 || isUserDoctor;

    $: isUserEnableSeeAllFilters = $user.role >= 2;

    $: isUserEnableCreateAndEdit = $user.role > 1 && $user.role != 90;

    $: isUserAdmin = $user.role == 100;

    $: showBatchButtons = selectedPatientEncounters.length > 0;

    $: canBatchChangeSurgeon = checkIfSelectedEncountersBelongsToSameDoctor(
        selectedPatientEncounters,
        selectedPatientEncountersIdArr,
    );

    $: columns = [
        {
            key: "selected?",
            title: "",
            headerClass: "toggleSelectAllHeader text-center",
            class: (v) =>
                `${v.status == DirectEncounterStatus.Cancelled ? " bg-[#E3E3E3]" : ""}`,
            renderComponent: {
                component: Checkbox,
            },
            csvExportHidden: true,
            stickyLeft: true,
        },
        {
            key: "patient",
            title: "Basic Info",
            headerClass: "text-left pl-4 sortable-header-decoration",
            class: (v) =>
                "py-1.5 cursor-pointer w-[300px]" +
                `${v.status == DirectEncounterStatus.Cancelled ? " bg-[#E3E3E3]" : ""}`,
            renderComponent: {
                component: BasicInfo,
            },
            csvExportHidden: true,
            stickyLeft: true,
        },
        {
            key: "id",
            title: "Enc. ID",
            headerClass:
                "text-left pl-4 cursor-pointer sortable-header-decoration min-w-[100px]",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            csvContent: (v) => getText(v.id),
            renderComponent: {
                component: DataId,
            },
        },
        {
            key: "patient",
            title: "Patient",
            csvContent: (v) => _getName(v.patient),
            isHidden: true,
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass:
                "text-left pl-4 cursor-pointer sortable-header-decoration",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getDoctor(v.doctor),
            renderComponent: {
                component: DoctorInfo,
            },
        },
        {
            key: "surgeon",
            title: "Surgeon",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getSurgeon(v.surgeon),
            renderComponent: {
                component: SurgeonInfo,
            },
        },
        {
            key: "hospital",
            title: "Hospital",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            csvContent: (v) => _getHospital(v.hospital, true),
            renderComponent: {
                component: HospitalInfo,
            },
        },
        {
            key: "scheduledDate",
            title: "Scheduled Date",
            headerClass:
                "text-left pl-4 cursor-pointer sortable-header-decoration",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getScheduleDate(v.scheduledDate),
            renderComponent: {
                component: ScheduledDateInfo,
            },
        },
        // {
        //     key: "claimAndSurveys",
        //     title: "Claim and Surveys",
        //     headerClass: "text-left pl-4 cursor-pointer",
        //     class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
        //     renderComponent: {
        //         component: ClaimAndSurveysInfo,
        //     },
        //     csvExportHidden: true,
        // },
        {
            key: "status",
            title: "Status",
            csvContent: (v) => getEncounterStatus(v.status),
            isHidden: true,
        },
        {
            key: "insuranceValidation",
            title: "Insurance",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderComponent: {
                component: InsuranceValidationInfo,
                props: {
                    onRerunOpvSuccess: async () => await reload()
                }
            },
            csvExportHidden: true,
        },
        {
            key: "claimStatus",
            title: "Claim Status",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => _getRowBgColor(v.checked),
            renderValue: (v) => getClaimStatus(v.claimStatus),
            renderComponent: {
                component: ClaimStatusInfo,
                props: {
                    onStatusChange: async () => await reload(),
                },
            },
        },
        {
            key: "encounterStatus",
            title: "Enc. Status",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => _getRowBgColor(v.checked),
            csvContent: (v) => getBillingStatus(v.billingStatus),
            renderComponent: {
                component: EncounterStatusInfo,
                props: {
                    onStatusChange: async () => await reload(),
                },
            },
            csvExportHidden: true,
        },
        {
            key: "billingStatus",
            title: "Billing Status",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => _getRowBgColor(v.checked),
            csvContent: (v) => getBillingStatus(v.billingStatus),
            renderComponent: {
                component: BillingStatusInfo,
            },
        },
        {
            key: "opvStatus",
            title: "OPV Status",
            headerClass: "text-left ml-3 cursor-default",
            class: (v) =>
                "text-center px-3 cursor-pointer" + _getRowBgColor(v.checked),
            csvContent: (v) => _renderOPVStatus(v, true),
            isHidden: true,
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-4 cursor-default rounded-tr-xl",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderComponent: {
                component: TableActions,
                props: {
                    saveTableScroll: () => saveTableScroll(),
                    afterModifyNotes: async () => await reload(),
                },
            },
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "scheduledDate", "patient", "doctor"];

    $: _getCheckboxString(selectedPatientEncountersIdArr);

    $: if (
        selectedPatientEncounters.length >= 0 &&
        returnPatientEncounterArr.length > 0
    ) {
        selectedPatientEncountersIdArr = selectedPatientEncounters.map(
            (patient) => patient.id,
        );
        _updateHeaderIcon();
    }

    $: isLoading =
        isGettingPatientEncounters ||
        isLoadingHealthFund ||
        isLoadingWorkCoverAuthorityFund ||
        isLoadingTrafficAccidentAuthority ||
        isLoadingAccountDoctors;

    let scrollTableContainerRef;
    const { saveScroll, restoreScrollPosition } = useTableScrollMemory(
        "patientEncounterTable",
    );

    onMount(async () => {
        getUrlWithoutSearch();

        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");
        await loadPatientEncounters(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        await loadHospitals();

        if (params.query) updateCheckboxByQuery(params.query);

        restoreTableScroll();
    });

    const redirectToCreatePage = () => {
        saveTableScroll();
        goToURL(`/PatientEncounter/create/new`);
    };

    const redirectToMultiCreatePage = () => {
        saveTableScroll();
        goToURL(`/PatientEncounter/create/multi`);
    };

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");
        if (arr.length) {
            if (isUserDoctorOrPatient) {
                if (isUserDoctor) await loadAccountDoctors();

                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].includes("page="))
                        page = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("size="))
                        pageSize = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("surgeonId=") && isUserDoctor) {
                        filters.surgeonId = Number(arr[i].split("=")[1]);

                        filterSurgeonObj = accountDoctorsArr.filter(
                            (surgeon) => surgeon.id == filters.surgeonId,
                        )[0];
                    }

                    if (arr[i].includes("hospitalId=")) {
                         searchHospitalsArr = arr[i]
                            .split("=")[1]
                            .split(",")
                            .map(Number);

                        handleHospitalChange(searchHospitalsArr, {
                            requireReloadFunction: false,
                        });
                    }

                    if (arr[i].includes("fromDate="))
                        filters.dateFrom = arr[i].split("=")[1];

                    if (arr[i].includes("toDate="))
                        filters.dateTo = arr[i].split("=")[1];

                    if (arr[i].includes("underpaid=1"))
                        filters.underpaid = true;

                    if (arr[i].includes("commsDisabled=1"))
                        filters.commsDisabled = true;

                    if (arr[i].includes("badDebtor="))
                        filters.badDebtor = Number(arr[i].split("=")[1]);
                    else filters.badDebtor = Number(-1);
                }
            }

            if (isUserEnableSeeAllFilters) {
                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].includes("page="))
                        page = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("size="))
                        pageSize = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("sort="))
                        filters.sort = arr[i].split("=")[1];

                    if (arr[i].includes("search="))
                        filters.search = arr[i].split("=")[1].toString();

                    // search encounter under patient - url from view patient details
                    if (arr[i].includes("patientId="))
                        filters.patientId = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("encounterId="))
                        filters.encounterId = arr[i].split("=")[1].toString();

                    if (arr[i].includes("status=")) {
                        filters.status = isNaN(arr[i].split("=")[1])
                            ? arr[i].split("=")[1]
                            : Number(arr[i].split("=")[1]);

                        searchEncounterStatusArr = getEncounterStatusArrayFromFilter(
                            filters.status,
                        );
                    }

                    if (arr[i].includes("billingStatus="))
                        filters.billingStatus = isNaN(arr[i].split("=")[1])
                            ? arr[i].split("=")[1]
                            : Number(arr[i].split("=")[1]);

                    if (arr[i].includes("handOver="))
                        filters.handOver = arr[i].split("=")[1];

                    if (arr[i].includes("claimStatus="))
                        filters.claimStatus = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("doctorId=")) {
                        if (isNaN(arr[i].split("=")[1])) {
                            filters.doctorId = arr[i].split("=")[1];
                            selectedDoctors = filters.doctorId
                                .split(",")
                                .map((id) => Number(id));
                        } else {
                            filters.doctorId = Number(arr[i].split("=")[1]);
                            selectedDoctors = [Number(filters.doctorId)];
                        }
                    }

                    if (arr[i].includes("quoteEncounterId="))
                        filters.quoteEncounterId = arr[i].split("=")[1];

                    if (arr[i].includes("fromDate="))
                        filters.dateFrom = arr[i].split("=")[1];

                    if (arr[i].includes("toDate="))
                        filters.dateTo = arr[i].split("=")[1];

                    if (arr[i].includes("patientInsuranceType=")) {
                        searchInsuranceTypeArr = arr[i]
                            .split("=")[1]
                            .split(",")
                            .map(Number);

                        handleInsuranceTypeChange(searchInsuranceTypeArr, {
                            requireReloadFunction: false,
                        });
                    }

                    if (arr[i].includes("patientInsuranceID="))
                        searchPrivateHealthFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("workCoverAuthorityID"))
                        searchWorkCoverAuthorityFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("trafficAccidentAuthorityID="))
                        searchTrafficAccidentFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("surgeonIDs="))
                        selectedAccountDoctors = arr[i]
                            .split("=")[1]
                            .split(",");

                     if (arr[i].includes("hospitalId="))
                        searchHospitalsArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("typeOfBill=")) {
                        searchTypeIfBillArr = arr[i].split("=")[1].split(",");

                        handleTypeOfBillChange(searchTypeIfBillArr, {
                            requireReloadFunction: false,
                        });
                    }

                    if (arr[i].includes("underpaid=1"))
                        filters.underpaid = true;

                    if (arr[i].includes("commsDisabled=1"))
                        filters.commsDisabled = true;

                    if (arr[i].includes("badDebtor="))
                        filters.badDebtor = Number(arr[i].split("=")[1]);
                    else filters.badDebtor = Number(-1);

                    if (arr[i].includes("paymentOption="))
                        filters.paymentOption = Number(arr[i].split("=")[1]);
                }
            }

            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (
                            !selectedPatientEncounters.some(
                                (pe) => pe.id == Number(ca),
                            )
                        ) {
                            let newPatientEncounter = { id: Number(ca) };
                            selectedPatientEncounters = [
                                ...selectedPatientEncounters,
                                newPatientEncounter,
                            ];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadInsuranceFund = async () => {
        isLoadingHealthFund = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheInsuranceFunds();

        _updateInsuranceFundsOptions();

        isLoadingHealthFund = false;
    };

    const loadWorkCoverAuthorityFund = async () => {
        isLoadingWorkCoverAuthorityFund = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheWorkersCompensation();

        _updateWorkCoverOptions();

        isLoadingWorkCoverAuthorityFund = false;
    };

    const loadTrafficAccidentFund = async () => {
        isLoadingTrafficAccidentAuthority = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheTrafficAccidentAuthorities();

        _updateTrafficAccidentOptions();

        isLoadingTrafficAccidentAuthority = false;
    };

    const loadAccountDoctors = async () => {
        isLoadingAccountDoctors = true;

        accountDoctorsArr = [];

        await apiMethods.getMethod
            .getAccountDoctors(0, 999999, {
                "filter[doctorId]": $user.id,
            })
            .then((res) => {
                if (res.results && res.results.length) {
                    res.results.map((accDoctor) => {
                        const { title, firstName, lastName, speciality } =
                            accDoctor;

                        accountDoctorsArr.push({
                            ...accDoctor,
                            displayName: `${getTitle(title)} ${firstName} ${lastName} (${speciality})`,
                        });
                    });
                }
            });

        isLoadingAccountDoctors = false;
    };

    const loadHospitals = async () => {
        isLoadingHospitals = true;

        hospitalsOptionsArr = [];

        await apiMethods.getMethod
            .getHospitals(0, 999999, {
            })
            .then((res) => {
                if (res.results && res.results.length) {
                    res.results.map((hospital) => {
                        const { id, name } =
                            hospital;

                        hospitalsOptionsArr.push({
                            key: id,
                            value: name
                        });
                    });
                }
            });

        isLoadingHospitals = false;
    };

    const loadPatientEncounters = async (start, limit, params) => {
        isGettingPatientEncounters = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        // searching encounter by id should exclude all other filters
        if (params["filter[id]"])
            params = { "filter[id]": params["filter[id]"] };

        await apiMethods.getMethod
            .getPatientEncounter(start, limit, params)
            .then((res) => _handlePatientEncounters(res))
            .then(() => _updateHeaderSortIcon());
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedPatientEncounters = [];

        updateURL();

        loadPatientEncounters(0, pageSize, _getSearchParams());
    };

    const handleEncounterStatusChange = (
        searchResult = [],
        { requireReloadFunction = true } = {},
    ) => {
        searchEncounterStatusArr = [...searchResult];

        filters.status = searchEncounterStatusArr.length
            ? searchEncounterStatusArr.join(",")
            : "";

        requireReloadFunction && handleFilterSelect();
    };

    const handleInsuranceTypeChange = async (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchInsuranceTypeArr = [...searchResult];

        if (!searchInsuranceTypeArr?.length) {
            isShowPrivateHealthFundSelect = false;
            searchPrivateHealthFundArr = [];

            isShowWorkCoverAuthoritySelect = false;
            searchWorkCoverAuthorityFundArr = [];

            isShowTrafficAccidentSelect = false;
            searchTrafficAccidentFundArr = [];
        } else {
            if (searchInsuranceTypeArr.includes(Number(0))) {
                isShowPrivateHealthFundSelect = true;

                // In order to get health func properly
                await loadInsuranceFund();
            } else {
                isShowPrivateHealthFundSelect = false;
                searchPrivateHealthFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(16))) {
                isShowWorkCoverAuthoritySelect = true;

                // In order to get work cover authority func properly
                await loadWorkCoverAuthorityFund();
            } else {
                isShowWorkCoverAuthoritySelect = false;
                searchWorkCoverAuthorityFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(17))) {
                isShowTrafficAccidentSelect = true;

                // In order to get traffic accident func properly
                await loadTrafficAccidentFund();
            } else {
                isShowTrafficAccidentSelect = false;
                searchTrafficAccidentFundArr = [];
            }
        }

        requireReloadFunction && handleFilterSelect();
    };

    const handleTypeOfBillChange = (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchTypeIfBillArr = [...searchResult];

        requireReloadFunction && handleFilterSelect();
    };

    const handleHospitalChange = (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchHospitalsArr = [...searchResult];

        requireReloadFunction && handleFilterSelect();
    };

    const handleHealthFundTypeChange = (searchResult = []) => {
        searchPrivateHealthFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleWorkCoverAuthorityTypeChange = (searchResult = []) => {
        searchWorkCoverAuthorityFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleTrafficAccidentFundTypeChange = (searchResult = []) => {
        searchTrafficAccidentFundArr = [...searchResult];

        handleFilterSelect();
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];
        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs rounded-md bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };
        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedPatientEncounters = [];
            _updateCheckBox();
        } else {
            returnPatientEncounterArr.forEach((encounter) => {
                if (!selectedPatientEncountersIdArr.includes(encounter.id)) {
                    _addSelectedPatientEncounter(encounter);
                }
            });
        }
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "selected?" && className.includes("checkbox"))
            _addSelectedPatientEncounter(rowData);
        else if (
            key !== "selected?" &&
            key !== "buttons" &&
            key !== "status" &&
            key != "billingStatus" &&
            key != "claimStatus" &&
            key != "encounterStatus" &&
            rowData.id
        ) {
            saveTableScroll();
            goToURL(`/PatientEncounter/view/${rowData.id}`);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadPatientEncounters(0, pageSize, _getSearchParams());
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPatientEncounters(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
        _updateCheckBox();
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadPatientEncounters(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/PatientEncounter/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/PatientEncounter/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");
        handleSelectSurgeonFromSearch([]); //reset surgeons
        //handleFilterSelect(); //no need, it's done on handle select surgeons
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleSelectSurgeonFromSearch = (searchResult = []) => {
        selectedAccountDoctors = [...searchResult];

        handleFilterSelect();
    };

    // HELPER FUNCTIONS
    const _handlePatientEncounters = (res) => {
        isGettingPatientEncounters = false;
        if (res.error) showError(res.error);
        else {
            returnPatientEncounterArr = [...res.results];
            count = res.total;
        }
        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 1);
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedPatientEncounters.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs rounded-md bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnPatientEncounterArr.every((patient) =>
                selectedPatientEncountersIdArr.includes(patient.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs rounded-md bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs rounded-md bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4px; border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _addSelectedPatientEncounter = (data) => {
        if (
            selectedPatientEncounters.some(
                (encounter) => encounter.id === data.id,
            )
        ) {
            selectedPatientEncounters = selectedPatientEncounters.filter(
                (encounter) => encounter.id !== data.id,
            );
            _updateCheckBox();
            returnPatientEncounterArr = returnPatientEncounterArr.map(
                (encounter) => {
                    if (encounter.id == data.id) encounter.checked = false;
                    return encounter;
                },
            );
        } else {
            selectedPatientEncounters = [...selectedPatientEncounters, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
            returnPatientEncounterArr = returnPatientEncounterArr.map(
                (encounter) => {
                    if (encounter.id == data.id) encounter.checked = true;
                    return encounter;
                },
            );
        }
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnPatientEncounterArr.forEach((encounter) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${encounter.id}`,
                );
                if (checkbox) {
                    if (selectedPatientEncountersIdArr.includes(encounter.id)) {
                        document.getElementById(
                            `selectCheckBoxId${encounter.id}`,
                        ).checked = true;
                        encounter.checked = true;
                    } else {
                        document.getElementById(
                            `selectCheckBoxId${encounter.id}`,
                        ).checked = false;
                        encounter.checked = false;
                    }
                }
            });
            _updateHeaderIcon();

            returnPatientEncounterArr = [...returnPatientEncounterArr];
        }, 1);
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (isUserEnableSeeAllFilters) {
            if (filters.search) params.search = filters.search;

            if (filters.encounterId) params["filter[id]"] = filters.encounterId;

            if (filters.patientId)
                params["filter[patientId]"] = filters.patientId;

            if (filters.status || filters.status == Number(0))
                params["filter[status]"] = filters.status;

            if (filters.billingStatus || filters.billingStatus == Number(0))
                params["filter[billingStatus]"] = filters.billingStatus;

            if (filters.handOver) params["filter[handOver]"] = filters.handOver;

            if (filters.claimStatus || filters.claimStatus == Number(0))
                params["filter[claimStatus]"] = filters.claimStatus;

            if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

            params = syncEncounterPaymentDropdownOptionFilter(
                params,
                filters.paymentOption,
            );
        }

        if (isUserDoctor && filters.surgeonId)
            params["filter[surgeonId]"] = filters.surgeonId;

        if (searchHospitalsArr && searchHospitalsArr.length > 0)
            params["filter[hospitalId]"] = searchHospitalsArr.join(",");

        if (filters.quoteEncounterId)
            params["filter[quoteEncounterId]"] = filters.quoteEncounterId;

        if (filters.dateFrom)
            params["filter[scheduledDateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[scheduledDateTo]"] = filters.dateTo;

        if (searchInsuranceTypeArr.length)
            params["filter[patientInsuranceType]"] =
                searchInsuranceTypeArr.join(",");

        if (searchPrivateHealthFundArr.length)
            params["filter[patientInsuranceID]"] =
                searchPrivateHealthFundArr.join(",");

        if (searchWorkCoverAuthorityFundArr.length)
            params["filter[patientWorkCoverAuthority]"] =
                searchWorkCoverAuthorityFundArr.join(",");

        if (searchTrafficAccidentFundArr.length)
            params["filter[patientTrafficAccidentAuthority]"] =
                searchTrafficAccidentFundArr.join(",");

        if (selectedAccountDoctors.length)
            params["filter[surgeonUniqueID]"] =
                selectedAccountDoctors.join(",");

        if (searchTypeIfBillArr.length)
            params["filter[typeOfBill]"] = searchTypeIfBillArr.join(",");

        if (filters.underpaid) params["filter[underpaid]"] = 1;

        if (filters.commsDisabled) params["filter[disableNotifications]"] = 1;

        if (!isNaN(filters.badDebtor) && Number(filters.badDebtor) >= 0)
            params["filter[badDebtor]"] = filters.badDebtor;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (isUserEnableSeeAllFilters) {
                if (params.sort) str += `&sort=${params.sort}`;

                if (params.search) str += `&search=${params.search}`;

                if (params["filter[id]"])
                    str += `&encounterId=${params["filter[id]"]}`;

                if (params["filter[patientId]"])
                    str += `&patientId=${params["filter[patientId]"]}`;

                if (
                    params["filter[status]"] ||
                    params["filter[status]"] == Number(0)
                )
                    str += `&status=${params["filter[status]"]}`;

                if (
                    params["filter[billingStatus]"] ||
                    params["filter[billingStatus]"] == Number(0)
                )
                    str += `&billingStatus=${params["filter[billingStatus]"]}`;

                if (params["filter[handOver]"])
                    str += `&handOver=${params["filter[handOver]"]}`;

                if (
                    (params["filter[claimStatus]"] ||
                        params["filter[claimStatus]"] == Number(0)) &&
                    typeof params["filter[claimStatus]"] == "number"
                )
                    str += `&claimStatus=${params["filter[claimStatus]"]}`;

                if (params["filter[doctorId]"])
                    str += `&doctorId=${params["filter[doctorId]"]}`;

                if (filters.paymentOption)
                    str += `&paymentOption=${filters.paymentOption}`;
            }

            if (params["filter[quoteEncounterId]"])
                str += `&quoteEncounterId=${params["filter[quoteEncounterId]"]}`;

            if (isUserDoctor && params["filter[surgeonId]"])
                str += `&surgeonId=${params["filter[surgeonId]"]}`;

            if (params["filter[scheduledDateFrom]"])
                str += `&fromDate=${params["filter[scheduledDateFrom]"]}`;

            if (params["filter[scheduledDateTo]"])
                str += `&toDate=${params["filter[scheduledDateTo]"]}`;

            if (params["filter[patientInsuranceType]"])
                str += `&patientInsuranceType=${params["filter[patientInsuranceType]"]}`;

            if (params["filter[patientInsuranceID]"])
                str += `&patientInsuranceID=${params["filter[patientInsuranceID]"]}`;

            if (params["filter[patientWorkCoverAuthority]"])
                str += `&workCoverAuthorityID=${params["filter[patientWorkCoverAuthority]"]}`;

            if (params["filter[patientTrafficAccidentAuthority]"])
                str += `&trafficAccidentAuthorityID=${params["filter[patientTrafficAccidentAuthority]"]}`;

            if (params["filter[surgeonUniqueID]"])
                str += `&surgeonIDs=${params["filter[surgeonUniqueID]"]}`;

            if (params["filter[hospitalId]"])
                str += `&hospitalId=${params["filter[hospitalId]"]}`;

            if (params["filter[typeOfBill]"])
                str += `&typeOfBill=${params["filter[typeOfBill]"]}`;

            if (params["filter[underpaid]"]) str += `&underpaid=1`;

            if (params["filter[disableNotifications]"])
                str += `&commsDisabled=1`;

            if (
                !isNaN(params["filter[badDebtor]"]) &&
                Number(params["filter[badDebtor]"]) >= 0
            )
                str += `&badDebtor=${params["filter[badDebtor]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                ["id", "scheduledDate", "patient", "doctor"];

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Enc. ID") ||
                        (params.sort == "scheduledDate" &&
                            row.childNodes[0].data == "Scheduled Date") ||
                        (params.sort == "patient" &&
                            row.childNodes[0].data == "Basic Info") ||
                        (params.sort == "doctor" &&
                            row.childNodes[0].data == "Doctor")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Enc. ID") ||
                        (params.sort == "scheduledDateDesc" &&
                            row.childNodes[0].data == "Scheduled Date") ||
                        (params.sort == "patientDesc" &&
                            row.childNodes[0].data == "Basic Info") ||
                        (params.sort == "doctorDesc" &&
                            row.childNodes[0].data == "Doctor")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getName = ({ firstName, lastName, title, middleName }) => {
        const name = `${firstName}${
            middleName ? " " + middleName : ""
        } ${lastName}`;

        return `${getTitle(title)} ${name}`;
    };

    const _getDoctor = ({ title, fullName }) => {
        return `${getTitle(title)} ${fullName}`;
    };

    const _getSurgeon = (surgeon) => {
        if (!surgeon || isObjectEmpty(surgeon))
            return "Missing surgeon details";

        const { title, firstName, lastName } = surgeon;

        return `${title ? getTitle(title) : ""} ${firstName ?? ""} ${
            lastName ?? ""
        }`;
    };

    const _getHospital = (hospital, isCsvContent = false) => {
        if (hospital) {
            const { name, address, suburb, state, postcode, providerNumber } =
                hospital;

            return isCsvContent
                ? `${name} - ${address}, ${suburb}, ${state}, ${postcode} (${providerNumber})`
                : `<div class="dropdown dropdown-top dropdown-hover">
                    <p>${name}${state ? `, ${state}` : ""}</p>
                    <div class="dropdown-content text-sm menu p-2 shadow bg-base-100 rounded-box w-64">
                        ${address}, ${suburb}, ${state}, ${postcode} (${providerNumber})
                    </div>
                </div>`;
        } else return "No hospital";
    };

    const _getScheduleDate = (date) => {
        if (date) {
            const dateArr = date.split("T")[0].split("-");
            if (Number(dateArr[0]) < 1900) return "-";
            return getFormatDateAndTime(date);
        } else return "-";
    };

    const _renderOPVStatus = (encounter, isCsvContent = false) => {
        const { patient, opvStatusMedicare, opvStatusHealthFund } = encounter;

        const greenText = "text-greenDefault",
            redText = "text-red-400",
            grayText = "text-gray-400";

        // In this case no direct insurance types would be detected, use prev logic
        if (!patient || isObjectEmpty(patient)) {
            const validStatus = [1, 2, 3];

            if (
                !validStatus.includes(opvStatusMedicare) &&
                !validStatus.includes(opvStatusHealthFund)
            )
                return isCsvContent
                    ? "Not checked"
                    : `<span class="${grayText}">Not checked</span>`;

            const result = [];

            if (validStatus.includes(opvStatusMedicare))
                result.push(
                    isCsvContent
                        ? "Medicare"
                        : `<span class="${
                              opvStatusMedicare == 1 ? greenText : redText
                          }">Medicare</span>`,
                );

            if (validStatus.includes(opvStatusHealthFund))
                result.push(
                    isCsvContent
                        ? "Health Fund"
                        : `<span class="${
                              opvStatusHealthFund == 1 ? greenText : redText
                          }">Health Fund</span>`,
                );

            return isCsvContent
                ? result.join("")
                : result.toString().replaceAll(",", "<br>");
        }

        const { insuranceType } = patient;

        // OPV medicare NOT CHECKED
        if (!opvStatusMedicare || opvStatusMedicare == 0)
            return isCsvContent
                ? `${getInsuranceType(insuranceType)}`
                : `<span class="${grayText}">${getInsuranceType(insuranceType)}</span>`;

        // Private checks status of HealthFund
        if (insuranceType == DirectInsuranceType.Private)
            return isCsvContent
                ? "Private"
                : `<span class="${opvStatusHealthFund == 1 ? `${greenText}` : `${redText}`}">Private</span>`;

        // All other insurance types check status of Medicare
        return isCsvContent
            ? "Medicare"
            : `<span class="${opvStatusMedicare == 1 ? `${greenText}` : `${redText}`}">Medicare</span>`;
    };

    const _renderActionIcons = ({ billingStatus, isDeleted }) => {
        const showEditBtn = !isDeleted;
        const showDuplicateBtn =
            (billingStatus != 4 && billingStatus != 5) ||
            (billingStatus == 4 && isUserEnableCreateAndEdit);

        return !showEditBtn && !showDuplicateBtn
            ? ""
            : `
            <div class="flex flex-1 items-center justify-center gap-x-2">
                ${
                    showEditBtn
                        ? `<div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Patient Encounter">
                            <i class="editIcon ${editIcon}"></i>
                        </div>`
                        : ""
                }
                ${
                    showDuplicateBtn
                        ? `<div class="duplicateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Duplicate Patient Encounter">
                            <i class="duplicateIcon ${duplicateIcon}"></i>
                        </div>`
                        : ""
                }
            </div>
        `;
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => fly(node, args);

    const reload = async () => {
        saveTableScroll();
        selectedPatientEncounters = [];
        await loadPatientEncounters(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );
        restoreTableScroll();
    };

    const checkIfSelectedEncountersBelongsToSameDoctor = (arr, idArr) => {
        if (arr.length) {
            // loaded selection from URL, no doctor id, can't verify
            if (!arr.every((a) => a.doctorId)) {
                bulkChangeSurgeonButtonToolTip =
                    "Unable to set surgeon - encounters from multiple doctors selected";
                return false;
            }

            const firstDocID = arr[0].doctorId;
            return arr.every((i) => i.doctorId == firstDocID);
        }

        bulkChangeSurgeonButtonToolTip =
            "Unable to set surgeon - encounters from multiple doctors selected";
        return false;
    };

    const bulkArchive = () => {
        bulkActionType = "archive";
        showBulkActionsModal = true;
    };

    const bulkChangeDate = () => {
        bulkActionType = "changeDate";
        showBulkActionsModal = true;
    };

    const bulkChangeSurgeon = () => {
        bulkActionType = "changeSurgeon";
        currentDoctorId = selectedPatientEncounters[0].doctorId;
        showBulkActionsModal = true;
    };

    const bulkChangeHospital = () => {
        bulkActionType = "changeHospital";
        showBulkActionsModal = true;
    };

    const bulkChangeDoctor = () => {
        bulkActionType = "changeEncounterDoctor";
        showBulkActionsModal = true;
    };

    const showBulkActions = () => {
        if (document.getElementById("bulkActionsContainer"))
            document.getElementById("bulkActionsContainer").style.display =
                "flex";
    };

    const hideBulkActions = () => {
        if (document.getElementById("bulkActionsContainer"))
            document.getElementById("bulkActionsContainer").style.display =
                "none";
    };

    const _updateInsuranceFundsOptions = () => {
        if (healthFundOptionsArr.length) return;

        healthFundOptionsArr = $insuranceFund.map((fund) => ({
            key: fund.id,
            value: fund.name,
        }));
    };

    const _updateWorkCoverOptions = () => {
        if (workCoverAuthorityOptionsArr.length) return;

        workCoverAuthorityOptionsArr = $workersCompensation.map((wc) => ({
            key: wc.id,
            value: wc.name,
        }));
    };

    const _updateTrafficAccidentOptions = () => {
        if (trafficAccidentOptionsArr.length) return;

        trafficAccidentOptionsArr = $trafficAccidentAuthorities.map((ta) => ({
            key: ta.id,
            value: ta.name,
        }));
    };

    const _getRowBgColor = (isChecked = false) =>
        isChecked ? " bg-[#F3F3F3]" : "";

    const saveTableScroll = () => {
        if (scrollTableContainerRef)
            saveScroll(
                scrollTableContainerRef.scrollTop,
                scrollTableContainerRef.scrollLeft,
            );
    };

    const restoreTableScroll = () => {
        if (!scrollTableContainerRef) return;

        scrollTableContainerRef.scrollTop = restoreScrollPosition().top;
        scrollTableContainerRef.scrollLeft = restoreScrollPosition().left;

        saveScroll();
    };

    const addEncounterOptions = [
        {
            label: 'SINGLE',
            disabled: false,
            onClick: () => redirectToCreatePage(),
        },
        {
            label: 'MULTIPLE',
            disabled: false,
            onClick: () => redirectToMultiCreatePage(),
        },
        {
            label: 'UPLOAD LIST',
            disabled: false,
            onClick: () => openUploadListModal(),
        }
    ];

    const openUploadListModal = () => {
        isUploadListModalOpen = true;
    }
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <div class="flex flex-row justify-between">
            {#if isUserEnableSeeAllFilters}
                <button
                    class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
                    on:click={toggleFilters}
                >
                    Filters
                    <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
                    <i
                        style="font-size:14px"
                        class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                        >&#xf104;
                    </i>
                </button>
            {/if}

            <div class="flex flex-row">
                {#if isUserEnableCreateAndEdit}
                    <div
                        class="flex flex-row flex-wrap gap-2 justify-end {$isMobile
                            ? ''
                            : 'max-w-[50%]'}"
                    >
                        {#if showBatchButtons}
                            <div
                                class="flex-none ml-2"
                                on:mouseleave={hideBulkActions}
                            >
                                <ul class="menu menu-horizontal p-0">
                                    <li tabindex="0" class="justify-end">
                                        <div
                                            class="flex"
                                            on:mouseenter={showBulkActions}
                                        >
                                            <div
                                                class="flex flex-col items-end"
                                            >
                                                Actions
                                            </div>
                                            <svg
                                                class="fill-current"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                                /></svg
                                            >
                                        </div>
                                        <ul
                                            id="dropdownMenu"
                                            class="z-[100] bg-zinc-200 border-zinc-300"
                                        >
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkArchive}
                                            >
                                                Archive Selection
                                            </Button>
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkChangeDate}
                                            >
                                                Change Date
                                            </Button>
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkChangeHospital}
                                            >
                                                Change Hospital
                                            </Button>
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkChangeSurgeon}
                                                isDisabled={!canBatchChangeSurgeon}
                                                disabledTooltip={bulkChangeSurgeonButtonToolTip}
                                            >
                                                Change Surgeon
                                            </Button>
                                            {#if isUserAdmin}
                                                <Button
                                                    manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                    clickFunc={bulkChangeDoctor}
                                                >
                                                    Change Doctor
                                                </Button>
                                            {/if}
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if isUserEnableSeeAllFilters}
                    <SearchSelect
                        bind:value={filters.billingStatus}
                        title="Billing Status"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                    >
                        <option value="all">All Statuses</option>
                        <option value="0,1,2,3,4" selected>
                            All Except Completed
                        </option>
                        {#each BillingStatus as billingStatusItem}
                            <option value={billingStatusItem.id}>
                                {billingStatusItem.type}
                            </option>
                        {/each}
                    </SearchSelect>
                {/if}

                <SearchSelect
                    bind:value={filters.paymentOption}
                    title="Payments"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientEncounters}
                >
                    <option value="">All</option>
                    {#each encounterPaymentDropdownOptions as { id, type }}
                        <option value={id}>
                            {type}
                        </option>
                    {/each}
                </SearchSelect>
            </div>
        </div>

        {#if showFilters && isUserEnableSeeAllFilters}
            <div
                class="flex items-end w-auto items-start gap-4 flex-wrap"
                in:fly={{ duration: 200, y: -50 }}
            >
                <div class="w-full">
                    <Button clickFunc={redirectToCreatePage}>
                        Create Patient Encounter
                    </Button>
                </div>

                <SearchInput
                    bind:value={filters.encounterId}
                    title="Encounter ID"
                    type="number"
                    inputFunc={() => {
                        if (!isNaN(filters.encounterId)) handleFilterSelect();
                    }}
                    id="encounterIdInput"
                    inputClass="bg-[#EEE]"
                />

                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                    inputClass="bg-[#EEE]"
                />

                {#if !isUserDoctor}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                        inputClass="bg-[#EEE]"
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32 bg-[#EEE]"
                        zIndex="100"
                    />
                {/if}

                <MultiSearchSelect
                    title="Status"
                    inputID="encounterStatusFilter"
                    isDisabled={isGettingPatientEncounters}
                    optionsArr={encounterStatus}
                    bind:searchResult={searchEncounterStatusArr}
                    onChangeFunc={handleEncounterStatusChange}
                    inputClass="w-48 bg-[#EEE]"
                    zIndex="101"
                />

                <SearchSelect
                    bind:value={filters.handOver}
                    title="Handover"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientEncounters}
                    inputClass="bg-[#EEE]"
                >
                    <option value="" selected>All</option>
                    <option value="1,2"> Invoices and Quotes </option>
                    <option value="2">Invoices</option>
                    <option value="1">Quotes</option>
                </SearchSelect>

                <SearchSelect
                    bind:value={filters.claimStatus}
                    title="Claiming Status"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientEncounters}
                    inputClass="bg-[#EEE]"
                >
                    <option value="" selected>All Statuses</option>
                    {#each claimStatus as claimStatusItem}
                        <option value={claimStatusItem.id}>
                            {claimStatusItem.type}
                        </option>
                    {/each}
                </SearchSelect>

                <SearchInput
                    bind:value={filters.quoteEncounterId}
                    title="From Patient Quote ID"
                    inputFunc={() => {
                        if (!isNaN(filters.quoteEncounterId))
                            handleFilterSelect();
                    }}
                    id="quoteEncounterIdInput"
                    inputClass="bg-[#EEE]"
                />

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.dateFrom}
                        bind:endDate={filters.dateTo}
                        title="Date Range"
                        inputClass="borderB bg-[#EEE] w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disabled={isGettingPatientEncounters}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.dateFrom}
                        title="Date from"
                        type="date"
                        inputClass="borderB bg-[#EEE]"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingPatientEncounters}
                        maxDate={filters.dateTo ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.dateTo}
                        title="Date to"
                        type="date"
                        inputClass="borderB bg-[#EEE]"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingPatientEncounters}
                        minDate={filters.dateFrom ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <MultiSearchSelect
                    title="Insurance Type"
                    inputID="patientInsuranceType"
                    isDisabled={isGettingPatientEncounters}
                    optionsArr={InsuranceType}
                    bind:searchResult={searchInsuranceTypeArr}
                    onChangeFunc={handleInsuranceTypeChange}
                    inputClass="w-60 bg-[#EEE]"
                    zIndex="99"
                />

                {#if isShowPrivateHealthFundSelect}
                    <MultiSearchSelect
                        title="Private Health Fund"
                        inputID="patientHealthFundType"
                        isDisabled={isGettingPatientEncounters ||
                            isLoadingHealthFund}
                        optionsArr={healthFundOptionsArr}
                        bind:searchResult={searchPrivateHealthFundArr}
                        onChangeFunc={handleHealthFundTypeChange}
                        zIndex="98"
                        inputClass="bg-[#EEE]"
                    />
                {/if}

                {#if isShowWorkCoverAuthoritySelect}
                    <MultiSearchSelect
                        title="Work Cover Authority"
                        inputID="patientWorkCoverAuthority"
                        isDisabled={isGettingPatientEncounters ||
                            isLoadingWorkCoverAuthorityFund}
                        optionsArr={workCoverAuthorityOptionsArr}
                        bind:searchResult={searchWorkCoverAuthorityFundArr}
                        onChangeFunc={handleWorkCoverAuthorityTypeChange}
                        zIndex="97"
                        inputClass="bg-[#EEE]"
                    />
                {/if}

                {#if isShowTrafficAccidentSelect}
                    <MultiSearchSelect
                        title="Traffic Accident Authority"
                        inputID="patientTrafficAccidentAuthority"
                        isDisabled={isGettingPatientEncounters ||
                            isLoadingTrafficAccidentAuthority}
                        optionsArr={trafficAccidentOptionsArr}
                        bind:searchResult={searchTrafficAccidentFundArr}
                        onChangeFunc={handleTrafficAccidentFundTypeChange}
                        zIndex="96"
                        inputClass="bg-[#EEE]"
                    />
                {/if}

                <SurgeonSelector
                    isDisabled={isGettingPatientEncounters}
                    bind:selectedSurgeonIds={selectedAccountDoctors}
                    onChangeFunc={handleSelectSurgeonFromSearch}
                    selectedDoctors={selectedDoctors}
                    inputClass="w-60 bg-[#EEE]"
                    zIndex="95"
                    bind:this={primarySurgeonSelectorRef}
                />

                {#if !isLoadingHospitals}
                    <MultiSearchSelect
                        title="Hospital"
                        inputID="encounterHospital"
                        isDisabled={isGettingPatientEncounters || isLoadingHospitals}
                        optionsArr={hospitalsOptionsArr}
                        bind:searchResult={searchHospitalsArr}
                        onChangeFunc={handleHospitalChange}
                        inputClass="w-60 bg-[#EEE]"
                        zIndex="94"
                    />
                {/if}

                <MultiSearchSelect
                    title="TypeOfBill"
                    inputID="encounterTypeOfBill"
                    isDisabled={isGettingPatientEncounters}
                    optionsArr={encounterBillingType}
                    bind:searchResult={searchTypeIfBillArr}
                    onChangeFunc={handleTypeOfBillChange}
                    inputClass="w-60 bg-[#EEE]"
                    zIndex="94"
                />

                <SearchSelect
                    bind:value={filters.badDebtor}
                    title="Bad Debtor"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientEncounters}
                    inputClass="bg-[#EEE]"
                >
                    <option value={Number(-1)} selected>All</option>
                    <option value={Number(0)}>Non-Bad Debtors</option>
                    <option value={Number(1)}> Bad Debtors </option>
                </SearchSelect>

                <div class="flex flex-row gap-x-[6px]">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={filters.underpaid}
                        id="underpaid"
                        on:change={handleFilterSelect}
                        disabled={isGettingPatientEncounters}
                    />
                    <label for="underpaid">Underpaid</label>
                </div>

                <div class="flex flex-row gap-x-[6px]">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={filters.commsDisabled}
                        id="disabledComms"
                        on:change={handleFilterSelect}
                        disabled={isGettingPatientEncounters}
                    />
                    <label for="disabledComms">Comms Disabled</label>
                </div>
            </div>
        {/if}

        <ResetFilterLink
            bind:filter={filters}
            specialVariables={{ 
                searchInsuranceTypeArr,
                searchTypeIfBillArr,
                searchPrivateHealthFundArr,
                selectedAccountDoctors,
                searchWorkCoverAuthorityFundArr,
                searchTrafficAccidentFundArr,
                searchHospitalsArr,
                searchEncounterStatusArr,
            }}
            onResetFilter={() => {
                selectedAccountDoctors = [];
                searchInsuranceTypeArr = [];
                searchTypeIfBillArr = [];
                handleInsuranceTypeChange(searchInsuranceTypeArr, {
                    requireReloadFunction: false,
                });
                handleTypeOfBillChange(searchTypeIfBillArr, {
                    requireReloadFunction: false,
                });
                searchPrivateHealthFundArr = [];
                searchHospitalsArr = [];
                searchWorkCoverAuthorityFundArr = [];
                searchTrafficAccidentFundArr = [];
                searchEncounterStatusArr = getEncounterStatusArrayFromFilter(
                    filters.status,
                );

                primarySurgeonSelectorRef?.clearSearchKeyword?.();
                secondarySurgeonSelectorRef?.clearSearchKeyword?.();
                clearFilterDoctor();
            }}
        />
    {:else}
        <div
            class="flex flex-0 items-start {$isMobile
                ? 'flex-col-reverse gap-y-4'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -50 }}
        >
            <div class="flex flex-row items-end justify-start flex-wrap gap-4">
                {#if isUserEnableSeeAllFilters}
                    <SearchInput
                        bind:value={filters.encounterId}
                        title="Encounter ID"
                        type="number"
                        inputFunc={() => {
                            if (!isNaN(filters.encounterId))
                                handleFilterSelect();
                        }}
                        id="encounterIdInput"
                        inputClass="bg-[#EEE]"
                    />

                    <SearchInput
                        bind:value={filters.search}
                        title="Search"
                        inputFunc={handleFilterSelect}
                        inputClass="bg-[#EEE]"
                    />

                    {#if !isUserDoctor}
                        <DefaultDoctorFilter
                            bind:filter={filters}
                            filterPropertyName={"doctorId"}
                            onChangeFunc={() => {
                                selectedDoctors =
                                    filters["doctorId"].split(",");
                                handleFilterSelect();
                            }}
                            inputClass="bg-[#EEE]"
                        />

                        <DoctorMultiSearchSelect
                            bind:searchResult={selectedDoctors}
                            onChangeFunc={handleSelectDoctor}
                            inputClass="w-32 bg-[#EEE]"
                            zIndex="100"
                        />
                    {/if}

                    <MultiSearchSelect
                        title="Status"
                        inputID="encounterStatusFilterCompact"
                        isDisabled={isGettingPatientEncounters}
                        optionsArr={encounterStatus}
                        bind:searchResult={searchEncounterStatusArr}
                        onChangeFunc={handleEncounterStatusChange}
                        inputClass="w-48 bg-[#EEE]"
                        zIndex="101"
                    />

                    <SearchSelect
                        bind:value={filters.billingStatus}
                        title="Billing Status"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                        inputClass="bg-[#EEE]"
                    >
                        <option value="all">All Statuses</option>
                        <option value="0,1,2,3,4" selected>
                            All Except Completed
                        </option>
                        {#each BillingStatus as billingStatusItem}
                            <option value={billingStatusItem.id}>
                                {billingStatusItem.type}
                            </option>
                        {/each}
                    </SearchSelect>

                    <SearchSelect
                        bind:value={filters.paymentOption}
                        title="Payments"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                        inputClass="bg-[#EEE]"
                    >
                        <option value="">All</option>
                        {#each encounterPaymentDropdownOptions as { id, type }}
                            <option value={id}>
                                {type}
                            </option>
                        {/each}
                    </SearchSelect>

                    <SearchSelect
                        bind:value={filters.handOver}
                        title="Handover"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                        inputClass="bg-[#EEE]"
                    >
                        <option value="" selected>All</option>
                        <option value="1,2"> Invoices and Quotes </option>
                        <option value="2">Invoices</option>
                        <option value="1">Quotes</option>
                        <option value="3">Pending Review</option>
                    </SearchSelect>

                    <SearchSelect
                        bind:value={filters.claimStatus}
                        title="Claiming Status"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                        inputClass="bg-[#EEE]"
                    >
                        <option value="" selected>All Statuses</option>
                        {#each claimStatus as claimStatusItem}
                            <option value={claimStatusItem.id}>
                                {claimStatusItem.type}
                            </option>
                        {/each}
                    </SearchSelect>

                    <SearchInput
                        bind:value={filters.quoteEncounterId}
                        title="From Patient Quote ID"
                        inputFunc={() => {
                            if (!isNaN(filters.quoteEncounterId))
                                handleFilterSelect();
                        }}
                        id="quoteEncounterIdInput"
                        inputClass="bg-[#EEE]"
                    />

                    {#if !$user.settings?.disableDateRangeInput}
                        <SearchDateRangeInput
                            bind:startDate={filters.dateFrom}
                            bind:endDate={filters.dateTo}
                            title="Date Range"
                            inputClass="borderB bg-[#EEE] w-[272px]"
                            on:change={handleFilterSelect}
                            showClearButton
                            disabled={isGettingPatientEncounters}
                            showScrollYearOptions
                        />
                    {:else}
                        <SearchInput
                            bind:value={filters.dateFrom}
                            title="Date from"
                            type="date"
                            inputClass="borderB bg-[#EEE]"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingPatientEncounters}
                            maxDate={filters.dateTo ?? undefined}
                            showScrollYearOptions
                        />

                        <SearchInput
                            bind:value={filters.dateTo}
                            title="Date to"
                            type="date"
                            inputClass="borderB bg-[#EEE]"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingPatientEncounters}
                            minDate={filters.dateFrom ?? undefined}
                            showScrollYearOptions
                        />
                    {/if}

                    <MultiSearchSelect
                        title="Insurance Type"
                        inputID="patientInsuranceType"
                        isDisabled={isGettingPatientEncounters}
                        optionsArr={InsuranceType}
                        bind:searchResult={searchInsuranceTypeArr}
                        onChangeFunc={handleInsuranceTypeChange}
                        inputClass="w-32 bg-[#EEE]"
                        zIndex="99"
                    />

                    {#if isShowPrivateHealthFundSelect}
                        <MultiSearchSelect
                            title="Private Health Fund"
                            inputID="patientHealthFundType"
                            isDisabled={isGettingPatientEncounters ||
                                isLoadingHealthFund}
                            optionsArr={healthFundOptionsArr}
                            bind:searchResult={searchPrivateHealthFundArr}
                            onChangeFunc={handleHealthFundTypeChange}
                            inputClass="w-32 bg-[#EEE]"
                            zIndex="98"
                        />
                    {/if}

                    {#if isShowWorkCoverAuthoritySelect}
                        <MultiSearchSelect
                            title="Work Cover Authority"
                            inputID="patientWorkCoverAuthority"
                            isDisabled={isGettingPatientEncounters ||
                                isLoadingWorkCoverAuthorityFund}
                            optionsArr={workCoverAuthorityOptionsArr}
                            bind:searchResult={searchWorkCoverAuthorityFundArr}
                            onChangeFunc={handleWorkCoverAuthorityTypeChange}
                            inputClass="w-32 bg-[#EEE]"
                            zIndex="97"
                        />
                    {/if}

                    {#if isShowTrafficAccidentSelect}
                        <MultiSearchSelect
                            title="Traffic Accident Authority"
                            inputID="patientTrafficAccidentAuthority"
                            isDisabled={isGettingPatientEncounters ||
                                isLoadingTrafficAccidentAuthority}
                            optionsArr={trafficAccidentOptionsArr}
                            bind:searchResult={searchTrafficAccidentFundArr}
                            onChangeFunc={handleTrafficAccidentFundTypeChange}
                            inputClass="w-32 bg-[#EEE]"
                            zIndex="96"
                        />
                    {/if}

                    <SurgeonSelector
                        isDisabled={isGettingPatientEncounters}
                        bind:selectedSurgeonIds={selectedAccountDoctors}
                        onChangeFunc={handleSelectSurgeonFromSearch}
                        selectedDoctors={selectedDoctors}
                        inputClass="w-32 bg-[#EEE]"
                        zIndex="95"
                        bind:this={secondarySurgeonSelectorRef}
                    />

                    {#if !isLoadingHospitals}
                        <MultiSearchSelect
                            title="Hospital"
                            inputID="encounterHospital"
                            isDisabled={isGettingPatientEncounters || isLoadingHospitals}
                            optionsArr={hospitalsOptionsArr}
                            bind:searchResult={searchHospitalsArr}
                            onChangeFunc={handleHospitalChange}
                            inputClass="w-60 bg-[#EEE]"
                            zIndex="94"
                        />
                    {/if}

                    <MultiSearchSelect
                        title="TypeOfBill"
                        inputID="encounterTypeOfBill"
                        isDisabled={isGettingPatientEncounters}
                        optionsArr={encounterBillingType}
                        bind:searchResult={searchTypeIfBillArr}
                        onChangeFunc={handleTypeOfBillChange}
                        inputClass="w-32 bg-[#EEE]"
                        zIndex="94"
                    />

                    <SearchSelect
                        bind:value={filters.badDebtor}
                        title="Bad Debtor"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                        inputClass="bg-[#EEE]"
                    >
                        <option value={Number(-1)}>All</option>
                        <option value={Number(0)}>Non-Bad Debtors</option>
                        <option value={Number(1)}> Bad Debtors </option>
                    </SearchSelect>

                    <div class="flex flex-row gap-x-[6px]">
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={filters.underpaid}
                            id="underpaid"
                            on:change={handleFilterSelect}
                            disabled={isGettingPatientEncounters}
                        />
                        <label for="underpaid">Underpaid</label>
                    </div>

                    <div class="flex flex-row gap-x-[6px]">
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={filters.commsDisabled}
                            id="disabledComms"
                            on:change={handleFilterSelect}
                            disabled={isGettingPatientEncounters}
                        />
                        <label for="disabledComms">Comms Disabled</label>
                    </div>

                    <ResetFilterLink
                        bind:filter={filters}
                        specialVariables={{
                            searchInsuranceTypeArr,
                            searchTypeIfBillArr,
                            searchPrivateHealthFundArr,
                            selectedAccountDoctors,
                            searchWorkCoverAuthorityFundArr,
                            searchTrafficAccidentFundArr,
                            searchHospitalsArr,
                            searchEncounterStatusArr,
                        }}
                        onResetFilter={() => {
                            selectedAccountDoctors = [];
                            searchInsuranceTypeArr = [];
                            searchTypeIfBillArr = [];
                            handleInsuranceTypeChange(searchInsuranceTypeArr, {
                                requireReloadFunction: false,
                            });
                            handleTypeOfBillChange(searchTypeIfBillArr, {
                                requireReloadFunction: false,
                            });
                            searchHospitalsArr = [];
                            searchPrivateHealthFundArr = [];
                            searchWorkCoverAuthorityFundArr = [];
                            searchTrafficAccidentFundArr = [];
                            searchEncounterStatusArr = getEncounterStatusArrayFromFilter(
                                filters.status,
                            );

                            primarySurgeonSelectorRef?.clearSearchKeyword?.();
                            secondarySurgeonSelectorRef?.clearSearchKeyword?.();
                            clearFilterDoctor();
                        }}
                    />
                {/if}
            </div>
            {#if isUserEnableCreateAndEdit}
                <div
                    class="flex lg:flex-row md:flex-col-reverse md:items-end flex-nowrap gap-2 justify-end'}"
                >
                    {#if showBatchButtons}
                        <div
                            class="flex-none ml-2"
                            on:mouseleave={hideBulkActions}
                        >
                            <ul class="menu menu-horizontal p-0">
                                <li tabindex="0" class="justify-end">
                                    <div
                                        class="flex"
                                        on:mouseenter={showBulkActions}
                                    >
                                        <div class="flex flex-col items-end">
                                            Actions
                                        </div>
                                        <svg
                                            class="fill-current"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            ><path
                                                d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                            /></svg
                                        >
                                    </div>
                                    <ul
                                        id="dropdownMenu"
                                        class="z-[100] border-zinc-300"
                                    >
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkArchive}
                                        >
                                            Archive Selection
                                        </Button>
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkChangeDate}
                                        >
                                            Change Date
                                        </Button>
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkChangeHospital}
                                        >
                                            Change Hospital
                                        </Button>
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkChangeSurgeon}
                                            isDisabled={!canBatchChangeSurgeon}
                                            disabledTooltip={bulkChangeSurgeonButtonToolTip}
                                        >
                                            Change Surgeon
                                        </Button>
                                        {#if isUserAdmin}
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                                clickFunc={bulkChangeDoctor}
                                            >
                                                Change Doctor
                                            </Button>
                                        {/if}
                                    </ul>
                                </li>
                            </ul>
                        </div>
                    {/if}
                    <!-- <Button
                        clickFunc={redirectToCreatePage}
                        manualClass="w-[240px]"
                    >
                        Create Patient Encounter
                    </Button> -->
                    <DropdownMenu
                        minWidth={200}
                        title="ADD ENCOUNTER"
                        items={addEncounterOptions}
                    ></DropdownMenu>
                </div>
            {/if}
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                bind:this={scrollTableContainerRef}
            >
                <CustomizeTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnPatientEncounterArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    tableConfig={{
                        bgColor: (v) => {
                            const { status } = v;

                            if (status == DirectEncounterStatus.Cancelled)
                                return "bg-[#E3E3E3]";
                            else return defaultTableRowBgColor;
                        },
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedPatientEncounters.length}
        tableData={returnPatientEncounterArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnPatientEncounterArr,
            fileName: "Patient Encounter",
            allDataApi: async () =>
                await apiMethods.getMethod.getPatientEncounter(
                    0,
                    999999,
                    _getSearchParams(),
                ),
            containerClass: "bg-[#EEE]",
        }}
    />
</div>

<PatientEncounterBulkActionsModal
    bind:isModalOpen={showBulkActionsModal}
    bind:bulkActionType
    {selectedPatientEncountersIdArr}
    doctorId={currentDoctorId}
    on:actionFinished={reload}
/>

<UploadPdfModal
    isModalOpen={isUploadListModalOpen}
    closeModal={() => (isUploadListModalOpen = false)}
    showDoctorSelector={isUserAdmin ? true : false}
    accountId={!isUserAdmin ? $loggedInDoctorDetail.id : null}
    onUploaded={reload}
/>
